import { useState, useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';

/**
 * InlineEditCell — iter404.
 *
 * Click → input → enter to save / esc to cancel. Used inline in the
 * Expenses detail rows so users can fix typos without opening the
 * full edit dialog. The component takes care of:
 *   - Single-click activation
 *   - Auto-focus + select-all on open
 *   - Enter to save, Escape or blur to cancel
 *   - Optimistic display (parent decides when to refetch)
 */
export function InlineEditCell({
  value,
  type = 'text',
  onSave,
  format = (v) => v,
  className = '',
  inputClassName = '',
  testid,
  step,
  minWidth = 60,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try { inputRef.current.select?.(); } catch { /* no-op */ }
    }
  }, [editing]);

  const cancel = () => { setEditing(false); setDraft(value); };
  const commit = async () => {
    if (saving) return;
    const newVal = type === 'number' ? Number(draft) : draft;
    if (newVal === value || newVal === '' || (type === 'number' && Number.isNaN(newVal))) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(newVal);
      setEditing(false);
    } catch { /* parent toasts */ }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className={`text-left hover:bg-info/10 rounded px-1 -mx-1 ${className}`}
        title="Click to edit"
        data-testid={testid ? `${testid}-display` : undefined}
        style={{ minWidth }}
      >
        {format(value)}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 align-middle" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type={type}
        step={step}
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        onBlur={() => setTimeout(cancel, 100)}
        className={`text-sm border border-info/40 rounded px-1.5 py-0.5 outline-none focus:border-info ${inputClassName}`}
        style={{ width: type === 'number' ? 80 : 160 }}
        data-testid={testid ? `${testid}-input` : undefined}
        disabled={saving}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={commit}
        className="text-success hover:text-success/80 disabled:opacity-50"
        disabled={saving}
        data-testid={testid ? `${testid}-save` : undefined}
        title="Save (Enter)"
      >
        <Check size={13} />
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={cancel}
        className="text-error hover:text-error/80"
        title="Cancel (Esc)"
      >
        <X size={13} />
      </button>
    </span>
  );
}

export default InlineEditCell;
